<template>
  <div ref="scroller" class="chat-messages" :aria-busy="isLoading.toString()">
    <div v-if="!messages.length && !isLoading" class="chat-messages__empty">
      <theme-icon icon="sparkles" type="icons" classes="w-6 h-6 chat-messages__empty-icon" />
      <p class="chat-messages__empty-title">Ask CRESiteIQ Assistant</p>
      <p class="chat-messages__empty-hint">
        Try: <em>"Which sites are underutilized?"</em>
        or <em>"Which leases are expiring soon?"</em>
      </p>
    </div>

    <div
      v-for="message in messages"
      :key="message.id"
      :class="[
        'chat-message',
        message.role === 'user' ? 'chat-message--user' : 'chat-message--assistant'
      ]"
    >
      <div class="chat-message__bubble" v-html="renderContent(message)"></div>
    </div>

    <div v-if="isLoading" class="chat-message chat-message--assistant">
      <div class="chat-message__bubble chat-typing">
        <span class="chat-typing__dot"></span>
        <span class="chat-typing__dot"></span>
        <span class="chat-typing__dot"></span>
      </div>
    </div>
  </div>
</template>

<script>
import DOMPurify from 'dompurify';

/**
 * Renders the active conversation as alternating user/assistant bubbles.
 *
 * Markdown links in assistant content (`[text](url)`) are converted to anchor
 * tags so deep links into /sites/{id}#leases or #utilization work. Everything
 * else is escaped — DOMPurify is the final guard against any HTML/JS sneaking
 * out of the LLM response.
 */
export default {
  name: 'ChatMessageList',

  computed: {
    messages() {
      return this.$store.state.chat.messages;
    },
    isLoading() {
      return this.$store.state.chat.isLoading;
    },
  },

  watch: {
    'messages.length'() {
      this.$nextTick(() => this.scrollToBottom());
    },
    isLoading() {
      this.$nextTick(() => this.scrollToBottom());
    },
  },

  methods: {
    renderContent(message) {
      const raw = message && message.content ? String(message.content) : '';

      // 1. Escape everything first.
      const escaped = this.escapeHtml(raw);

      // 2. Convert markdown tables to HTML tables.
      const withTables = this.renderTables(escaped);

      // 3. Convert markdown bold (**text**) to <strong>.
      const withBold = withTables.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

      // 4. Convert markdown links: [text](url) → safe <a> tag.
      //    URL must start with `/` (internal) or `https://` to be allowed.
      const linkified = withBold.replace(
        /\[([^\]]+)\]\((\/[^\s)]*|https:\/\/[^\s)]+)\)/g,
        (_, text, href) => `<a href="${href}" class="chat-message__link">${text}</a>`,
      );

      // 5. Preserve newlines visually. The regex skips \n that immediately precede
      //    an HTML table tag so we don't inject <br> inside the table markup.
      const withBreaks = linkified.replace(/\n(?!<\/?(table|thead|tbody|tr|th|td))/g, '<br>');

      // 6. Final sanitisation pass.
      return DOMPurify.sanitize(withBreaks, {
        ALLOWED_TAGS: ['a', 'br', 'em', 'strong', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'code', 'div'],
        ALLOWED_ATTR: ['href', 'class'],
      });
    },

    /**
     * Convert a markdown pipe table into an HTML table.
     * Handles the standard GFM format:
     *   | Col1 | Col2 |
     *   |------|------|
     *   | val1 | val2 |
     */
    renderTables(text) {
      // Split into lines, group consecutive pipe-table lines, convert each group.
      const lines = text.split('\n');
      const out = [];
      let tableLines = [];

      const flushTable = () => {
        if (tableLines.length < 2) {
          // Not enough lines to be a real table — emit as-is.
          out.push(...tableLines);
          tableLines = [];
          return;
        }

        // Find the separator row (---|---).
        const sepIdx = tableLines.findIndex(l => /^\|[\s\-|:]+\|$/.test(l.trim()));
        if (sepIdx < 1) {
          out.push(...tableLines);
          tableLines = [];
          return;
        }

        const parseRow = (line) =>
          line.trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim());

        const headers = parseRow(tableLines[0]);
        const rows = tableLines.slice(sepIdx + 1).filter(l => l.trim().startsWith('|'));

        let html = '<div class="chat-table-wrap"><table class="chat-table"><thead><tr>';
        headers.forEach(h => { html += `<th>${h}</th>`; });
        html += '</tr></thead><tbody>';
        rows.forEach(row => {
          const cells = parseRow(row);
          html += '<tr>';
          cells.forEach(c => { html += `<td>${c}</td>`; });
          html += '</tr>';
        });
        html += '</tbody></table></div>';
        out.push(html);
        tableLines = [];
      };

      lines.forEach(line => {
        if (line.trim().startsWith('|')) {
          tableLines.push(line);
        } else {
          if (tableLines.length) flushTable();
          out.push(line);
        }
      });
      if (tableLines.length) flushTable();

      return out.join('\n');
    },

    escapeHtml(s) {
      return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },

    scrollToBottom() {
      const el = this.$refs.scroller;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    },
  },
};
</script>
